import React, { useCallback, useContext, useEffect, useReducer } from 'react';

import { FormControlLabel, Grid, InputAdornment, Tooltip } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import InfoIcon from '@material-ui/icons/Info';

import { ReloadContext } from 'src/contexts/ReloadContext';
import { UserContext } from 'src/contexts/UserContext';
import useAccounts from 'src/hooks/useAccounts';
import useCatchAPIError from 'src/hooks/useCatchAPIError';
import useFilterQuery from 'src/hooks/useFilterQuery';
import apiService from 'src/services/api.service';
import helpService from 'src/services/help.service';
import DelayChangedTextField2 from 'src/wrappers/DelayChangedTextField2';

import CustomCheckbox from '../CustomCheckbox';
import DateBasedOnControl from './DateBasedOnControl';
import PeriodsControl from './PeriodsControl';
import PurchaseOrderDataTableFilterModelFactory from './PurchaseOrderDataTableFilterModelFactory';
import { dataTableFilterInitialState, purchaseOrderDataTableFilterReducer } from './PurchaseOrderDataTableFilterReducer';
import SuppliersControl from './SuppliersControl';

const useStyles = makeStyles((theme) => ({
  root: {
    // marginTop: theme.spacing(1),
    // marginBottom: theme.spacing(1),
  },
  formControl: {
    width: '100%',
  },
  checkBlockControl: {
    paddingTop: '6px',
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  checkBox: {
    marginRight: theme.spacing(1),
  },
  icon: {
    color: '#888888',
  },
  specialSearch: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    fontSize: '1rem',
    alignItems: 'center',
    '& b': {
      paddingRight: theme.spacing(1),
    },
  },
  filterTag: {
    position: 'relative',
    padding: 5,
    paddingLeft: 12,
    paddingRight: 30,
    backgroundColor: theme.table.filterTag.color,
    color: 'white',
    borderRadius: 16,
    margin: 2,
    '& svg': {
      position: 'absolute',
      right: 5,
      top: 5,
      fontSize: '1.2rem',
      color: '#1c1c1c',
      cursor: 'pointer',
      backgroundImage: 'linear-gradient(#fff,#fff)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '50% 50%',
      backgroundPosition: 'center',
      '&:hover': {
        color: '#636363',
      },
    },
  },
}));

const PurchaseOrderDataTableFilter = (props) => {
  const {
    showSuppliers = false,
    showFreeText = true,
    showDateBasedOn = true,
    sendQueryStringsNoFreeTextSearch,
    freeTextFilterOn = 'Order Number, Supplier, Deliver To, Shipment Status, Product Code and Product Description.',
  } = props;

  const classes = useStyles();
  const [userContext] = useContext(UserContext);
  const [reloadContext] = useContext(ReloadContext);
  const { filterQuery, queryString, updateAddressUrl } = useFilterQuery();
  const { accountId, accountName } = useAccounts();
  const [state, dispatch] = useReducer(purchaseOrderDataTableFilterReducer, dataTableFilterInitialState);
  const { catchApiError } = useCatchAPIError();

  useEffect(() => {
    if (sendQueryStringsNoFreeTextSearch) {
      sendQueryStringsNoFreeTextSearch(queryString);
    }
  }, [queryString]);

  useEffect(() => {
    let initialReducerState = PurchaseOrderDataTableFilterModelFactory.createReducerStateFromFilterQueryModel(filterQuery, showDateBasedOn);
    dispatch({
      type: 'initialFields',
      payload: initialReducerState,
    });
  }, []);

  useEffect(() => {
    const resetFilters = (selectedAccountId, selectedDateBasedOn) => {
      apiService
        .getPurchaseOrderTableFilters(selectedAccountId, selectedDateBasedOn)
        .then((result) => {
          //Periods
          let periods = null;
          if (result.data.filterPeriods) {
            periods = result.data.filterPeriods;
          }

          //Suppliers
          let suppliers = null;
          if (result.data.reportSuppliers) {
            let tempSuppliers = result.data.reportSuppliers.sort(function (a, b) {
              var nameA = a.name.toLowerCase(),
                nameB = b.name.toLowerCase();
              if (nameA < nameB) return -1;
              if (nameA > nameB) return 1;
              return 0;
            });

            suppliers = tempSuppliers.map((supplier) => {
              return { id: supplier.name, name: supplier.name };
            });

            suppliers.forEach((item) => {
              item.name = helpService.truncateString(item.name, 40);
            });
          }

          dispatch({
            type: 'loadSelectItems',
            payload: {
              periods: periods,
              suppliers: suppliers,
            },
          });
        })
        .catch(catchApiError);
    };
    if (accountId && state.dateBasedOn) {
      resetFilters(accountId, state.dateBasedOn);
    }
  }, [reloadContext, accountId, state.dateBasedOn]);

  useEffect(() => {
    function ResendFilterComponentData() {
      const filterComponentData = PurchaseOrderDataTableFilterModelFactory.createReturnFilterComponentData(state, accountId, accountName, userContext.buyerId);
      props.sendFilterComponentData(filterComponentData);
      console.log('ResendFilterComponentData', filterComponentData);
    }

    if (state.periods != null) {
      let filterQueryModel = PurchaseOrderDataTableFilterModelFactory.createFilterQueryModelFromReducerState(state, accountId);
      updateAddressUrl(filterQueryModel);
      ResendFilterComponentData();
    }
  }, [state.filterFieldsUpdated]);

  const clearSpecialSearch = useCallback((e) => {
    dispatch({
      type: 'clearSpecialSearch',
    });
  }, []);

  const handleStartPeriodChange = useCallback((e) => {
    dispatch({
      type: 'fieldWithTrigger',
      fieldName: 'startPeriodValue',
      payload: e.target.value,
    });
  }, []);

  const handleEndPeriodChange = useCallback((e) => {
    dispatch({
      type: 'fieldWithTrigger',
      fieldName: 'endPeriodValue',
      payload: e.target.value,
    });
  }, []);

  const handleSupplierValuesChange = useCallback((e) => {
    dispatch({
      type: 'fieldWithTrigger',
      fieldName: 'supplierId',
      payload: e.target.value,
    });
  }, []);

  const handleFreeTextValueChange = useCallback((text) => {
    dispatch({
      type: 'fieldWithTrigger',
      fieldName: 'freeTextSearch',
      payload: text,
    });
  }, []);

  const handleDateBasedOnChange = useCallback((e) => {
    dispatch({
      type: 'field',
      fieldName: 'dateBasedOn',
      payload: e.target.value,
    });
  }, []);

  const handleFilterByMyBuyerIdChange = useCallback(() => {
    dispatch({
      type: 'fieldWithTrigger',
      fieldName: 'filterByMyBuyerId',
      payload: !state.filterByMyBuyerId,
    });
  }, [state.filterByMyBuyerId]);

  const disableStartPeriodMenuItem = useCallback(
    (periodItem) => {
      if (state.endPeriodValue) {
        let selectedEndPeriod = state.periods.find((x) => x.id === state.endPeriodValue);
        if (selectedEndPeriod) {
          let endDate = new Date(selectedEndPeriod.periodEnd);
          let startDate = new Date(periodItem.periodStart);
          return startDate >= endDate;
        }
      }
      return false;
    },
    [state.periods, state.endPeriodValue]
  );

  const disableEndPeriodMenuItem = useCallback(
    (periodItem) => {
      if (state.startPeriodValue) {
        let selectedStartPeriod = state.periods.find((x) => x.id === state.startPeriodValue);
        if (selectedStartPeriod) {
          let startDate = new Date(selectedStartPeriod.periodStart);
          let endDate = new Date(periodItem.periodEnd);
          return startDate >= endDate;
        }
      }
      return false;
    },
    [state.periods, state.startPeriodValue]
  );

  return (
    <Grid container direction="row" spacing={2} className={classes.root}>
      {state.specialSearch?.title && (
        <Grid item xs={12} className="align-self-center">
          <h5 className={classes.specialSearch}>
            <b>Filter:</b>{' '}
            <div className={classes.filterTag}>
              {state.specialSearch?.title} <CancelIcon onClick={clearSpecialSearch} />
            </div>
          </h5>
        </Grid>
      )}

      {userContext.buyerId && (
        <Grid item xs={12}>
          <FormControl variant="outlined" className={classes.formControl + ' ' + classes.checkBlockControl + ' exclude'}>
            <FormControlLabel
              className={classes.checkBox}
              checked={state.filterByMyBuyerId}
              onChange={handleFilterByMyBuyerIdChange}
              control={<CustomCheckbox name="filterByMyBuyerId" color="primary" />}
              label="Show only my Purchase Orders."
            />
            <Tooltip arrow placement="top-end" title="Filter by your Buyer Id, you can set it on user's profile page.">
              <InfoIcon className={classes.icon} />
            </Tooltip>
          </FormControl>
        </Grid>
      )}

      {showSuppliers && (
        <Grid item xs={12} sm={6} lg={3} xl>
          <SuppliersControl selectedSupplierValues={state.supplierId} handleSupplierValuesChange={handleSupplierValuesChange} suppliers={state.suppliers} />
        </Grid>
      )}

      {showDateBasedOn && (
        <Grid item xs={12} lg={1}>
          <DateBasedOnControl selectedDateBasedOn={state.dateBasedOn} handleDateBasedOnChange={handleDateBasedOnChange} />
        </Grid>
      )}

      <Grid item xs={12} sm={6} lg={2}>
        <PeriodsControl
          id={'period-start'}
          title={'Start'}
          selectedPeriodValue={state.startPeriodValue}
          handlePeriodChange={handleStartPeriodChange}
          periods={state.periods}
          disablePeriodMenuItem={disableStartPeriodMenuItem}
        />
      </Grid>

      <Grid item xs={12} sm={6} lg={2}>
        <PeriodsControl
          id={'period-end'}
          title={'End'}
          selectedPeriodValue={state.endPeriodValue}
          handlePeriodChange={handleEndPeriodChange}
          periods={state.periods}
          disablePeriodMenuItem={disableEndPeriodMenuItem}
        />
      </Grid>

      {showFreeText && (
        <Grid item xs={12} sm>
          <FormControl variant="outlined" className={classes.formControl + ' exclude'}>
            <DelayChangedTextField2
              id="free-text-filter"
              label="Free Text Filter"
              variant="outlined"
              value={state.freeTextSearch}
              onValueChange={(text) => {
                dispatch({
                  type: 'field',
                  fieldName: 'freeTextSearch',
                  payload: text,
                });
              }}
              onDelayValueChange={handleFreeTextValueChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip arrow title={`Filter on: ${freeTextFilterOn}`}>
                      <InfoIcon className={classes.icon} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

export default PurchaseOrderDataTableFilter;
